<template>
    <div class="container">
        <div class="header">
            <h1>History</h1>
            <Button
                class="close"
                severity="secondary"
                aria-label="Close"
                icon="pi pi-times"
                @click="
                    router.push({
                        name: route.name,
                        params: {
                            ...route.params,
                            version: undefined,
                        },
                    })
                "
            />
        </div>
        <!-- Side-by-side only renders on views that draw two versions (documents,
             BPMN diffs); hidden elsewhere so it isn't an inert toggle. -->
        <div v-if="supportsSideBySide" class="action-buttons">
            <div class="action-button switch">
                <ToggleSwitch
                    class="toggleswitch"
                    inputId="compare"
                    v-model="compare"
                />
                <label for="compare">Side-by-side</label>
            </div>
        </div>
        <div class="groups">
            <div
                v-for="(group, i) in groupedHistoryEntries"
                :key="i"
                class="group"
            >
                <div class="description">
                    <small
                        ><b>{{ group.description }}</b></small
                    >
                </div>
                <!-- The rail line is drawn on this container; each entry paints
                     its own node dot over it. -->
                <div class="group-timeline">
                    <HistoryTimelineEntry
                        v-for="commit in group.commits"
                        :key="commit.id"
                        :commit="commit"
                        :is-selected="isSelected(commit)"
                        :is-current="commit.id === lastCommit?.id"
                        :is-comparison="isComparison(commit)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import HistoryTimelineEntry from "@/components/HistoryTimelineEntry.vue";
import { useEditManager } from "@/composables/edit-manager";
import type { HistoryEntryWithDate } from "@/lib/artefacts/user";
import { useHistoryManager } from "@/stores/history";

const { isEditing } = useEditManager();
const route = useRoute();
const router = useRouter();
const { selectedCommit, previousCommit, groupedHistoryEntries, lastCommit } =
    useHistoryManager();

// A commit is "selected" when it's the viewed version, or when we're on the
// live "current" view (and not mid-edit) and it's the latest commit.
const isSelected = (commit: HistoryEntryWithDate): boolean =>
    commit.id === selectedCommit.value?.id ||
    (route.params.version === "current" &&
        !isEditing.value &&
        commit.id === lastCommit.value?.id);

// In side-by-side mode the version *before* the selected one is the comparison.
const isComparison = (commit: HistoryEntryWithDate): boolean =>
    compare.value && commit.id === previousCommit.value?.id;

const toggleCompare = () => {
    router.push({
        name: route.name,
        params: route.params,
        query: {
            ...route.query,
            compare: route.query.compare === undefined ? "1" : undefined,
        },
    });
};

const compare = computed({
    get() {
        return route.query.compare !== undefined;
    },
    set() {
        toggleCompare();
    },
});

// Only the document and BPMN-definition views actually render the "before"
// version beside the current one; on rich entity pages the toggle does nothing,
// so hide it there (opt-in per route via meta.supportsSideBySide).
const supportsSideBySide = computed(
    () => route.meta.supportsSideBySide === true
);
</script>

<style lang="scss" scoped>
.container {
    overflow-y: auto;
    /* Minus the header height. */
    max-height: calc(100vh - 57px);
    flex: 1;
    display: flex;
    flex-direction: column;
}

.header {
    position: sticky;
    top: 0;
    padding: 1rem;
    /* Same as margin-top which is hardcoded in typography.scss, we should
    think about handling spacing in a nicer way. Using padding to keep
    reserving the space even though the user scrolls. */
    padding-top: 0.375em;
    display: flex;
    align-items: flex-end;
    justify-content: space-evenly;
    /* So text won't appear right behind the header.
       Docs on Canvas: https://web.dev/articles/color-scheme */
    background-color: Canvas;

    h1 {
        margin-bottom: 0;
        flex: 1;
        text-align: center;
    }

    .close {
        flex-grow: 0;
    }
}

.groups {
    flex: 1;
}

.group {
    margin-bottom: 8px;

    .description {
        margin-left: 8px;
        margin-bottom: 4px;
    }
}

/* Draws the continuous vertical rail; each HistoryTimelineEntry paints its own
   node dot over the line at left: 3px + 6px (dot centre). */
.group-timeline {
    position: relative;

    &::before {
        content: "";
        position: absolute;
        left: 9px;
        top: 12px;
        bottom: 12px;
        width: 2px;
        background: var(--surface-border);
    }
}

.action-buttons {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    flex-direction: column;

    .switch {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
    }
}
</style>
